/*
 * Legacy customer history — read seam + pure formatters (WU
 * PROJ-0062-WU-LEGACY-HISTORY-01; LOCKED PROGRAMME-DEC-LEGACY-CUSTOMER-
 * MIGRATION-V1 sinks 2/5).
 *
 * VISIBILITY (all must hold, else render NOTHING — never an error UI):
 *   flag VITE_LEGACY_HISTORY_ENABLED === 'true' AND a session + my-api base AND
 *   a 200 well-formed GET /me/history AND legacy_status === 'linked' AND
 *   legacy !== null.
 *
 * MONEY (ruling 1): gross receipts per currency, refunds NOT netted — labelled
 * "Total paid", never "net". Each currency is its own line: never converted,
 * never summed across currencies. Minor → major via the currency's own
 * fraction digits as Intl reports them (GBP 2, JPY 0, KWD 3 …).
 */
import { useEffect, useState } from 'react'
import { loadSession } from '../auth/session'
import { legacyHistoryEnabled } from '../lib/legacyHistoryFlag'
import type { Lang } from '../i18n/useLocale'
import { fetchMyHistory, myApiBase, type MyHistory, type MyLegacyHistory } from './myApi'

/** The legacy block to render, or null (not linked / unknown / absent). */
export function visibleLegacyHistory(h: MyHistory | null): MyLegacyHistory | null {
  return h && h.legacy_status === 'linked' ? h.legacy : null
}

/** Formatting locale — mirrors the app's existing convention
 *  (EligibilityMismatchNotice: 'ar' under Arabic, else 'en-GB'). */
const localeFor = (lang: Lang): string => (lang === 'ar' ? 'ar' : 'en-GB')

/** 123450, 'GBP' → '£1,234.50'; 45000, 'AED' → 'AED 450.00'. */
export function formatMinorAmount(minor: number, currency: string, lang: Lang): string {
  try {
    const nf = new Intl.NumberFormat(localeFor(lang), { style: 'currency', currency })
    const digits = nf.resolvedOptions().maximumFractionDigits ?? 2
    return nf.format(minor / 10 ** digits)
  } catch {
    // Unknown code to this runtime's Intl — show the honest raw value.
    return `${(minor / 100).toFixed(2)} ${currency}`
  }
}

/** 'YYYY-MM-DD' as a PLAIN calendar date — built and formatted in UTC so no
 *  viewer timezone can shift it a day. '2019-03-14' → '14 Mar 2019'. */
export function formatPlainDate(ymd: string, lang: Lang): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return ymd
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toLocaleDateString(localeFor(lang), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** Read the caller's legacy history once per mount. null until (and unless) a
 *  visible linked block arrives; flag off ⇒ no request at all. */
export function useLegacyHistory(env: Record<string, string | undefined> = import.meta.env): MyLegacyHistory | null {
  const enabled = legacyHistoryEnabled(env)
  const base = myApiBase(env)
  const [legacy, setLegacy] = useState<MyLegacyHistory | null>(null)
  useEffect(() => {
    if (!enabled || !base) return
    const session = loadSession()
    if (!session) return
    let cancelled = false
    void fetchMyHistory(session.idToken, base).then((h) => {
      if (!cancelled) setLegacy(visibleLegacyHistory(h))
    })
    return () => { cancelled = true }
  }, [enabled, base])
  return enabled ? legacy : null
}
